/**
 * usePaymentRequisites Hook
 *
 * Fetches payment requisites using URQL.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const PaymentRequisitesDocument = `
  query PaymentRequisites {
    paymentRequisites {
      id
      currency
      bank
      account
      name
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface PaymentRequisites {
  id: string;
  currency: string;
  bank: string;
  account: string;
  name: string;
}

interface UsePaymentRequisitesResult {
  requisites: PaymentRequisites[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch payment requisites.
 *
 * Uses URQL for fetching available payment requisites.
 */
export function usePaymentRequisites(): UsePaymentRequisitesResult {
  const [result, reexecuteQuery] = useQuery<{
    paymentRequisites: PaymentRequisites[];
  }>({
    query: PaymentRequisitesDocument,
  });

  return {
    requisites: result.data?.paymentRequisites ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
